import {
  TrainingProgramType,
  DifficultyLevel,
  WorkoutDuration,
} from "../types/program";
import { badge } from "./badgeColors";

const toTitleCase = (value: string): string => {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Get readable label for training program type
export const getTrainingProgramTypeLabel = (
  programType: TrainingProgramType
): string => {
  const labels: Record<TrainingProgramType, string> = {
    [TrainingProgramType.CARDIO]: "Cardio",
    [TrainingProgramType.MOBILITY_AND_WARM_UP]: "Mobility and Warm-up",
    [TrainingProgramType.HYPERTROPHY]: "Hypertrophy",
    [TrainingProgramType.HYBRID]: "Hybrid",
    [TrainingProgramType.STRENGTH]: "Strength",
    [TrainingProgramType.OTHER]: "Other",
  };

  return labels[programType];
};

// Get readable label for difficulty level
export const getDifficultyLevelLabel = (
  difficultyLevel: DifficultyLevel | string
): string => {
  const labels: Record<DifficultyLevel, string> = {
    [DifficultyLevel.BEGINNER]: "Beginner",
    [DifficultyLevel.INTERMEDIATE]: "Intermediate",
    [DifficultyLevel.ADVANCED]: "Advanced",
  };

  return (
    labels[difficultyLevel as DifficultyLevel] || toTitleCase(difficultyLevel)
  );
};

const difficultyLevelOrder: DifficultyLevel[] = [
  DifficultyLevel.BEGINNER,
  DifficultyLevel.INTERMEDIATE,
  DifficultyLevel.ADVANCED,
];

const difficultyLevelOrderMap = difficultyLevelOrder.reduce<
  Record<string, number>
>((acc, level, index) => {
  acc[level] = index;
  return acc;
}, {});

export const normalizeDifficultyLevels = (
  difficultyLevel?:
    | DifficultyLevel
    | DifficultyLevel[]
    | string
    | string[]
    | null
): DifficultyLevel[] => {
  if (!difficultyLevel) return [];
  let levels: Array<string | DifficultyLevel>;

  if (
    typeof difficultyLevel === "string" &&
    difficultyLevel.trim().startsWith("[")
  ) {
    try {
      const parsed = JSON.parse(difficultyLevel);
      levels = Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      levels = [difficultyLevel];
    }
  } else if (typeof difficultyLevel === "string") {
    // Handle comma-separated strings (e.g., "beginner,intermediate,advanced")
    levels = difficultyLevel.split(",").map((level) => level.trim());
  } else {
    levels = Array.isArray(difficultyLevel)
      ? difficultyLevel
      : [difficultyLevel];
  }

  const normalizedLevels = levels
    .flatMap((level) => {
      // Additional safety: split any remaining comma-separated strings in the array
      if (typeof level === "string" && level.includes(",")) {
        return level
          .split(",")
          .map((l) => l.trim().toLowerCase() as DifficultyLevel);
      }
      return level?.toString().toLowerCase() as DifficultyLevel;
    })
    .filter((level): level is DifficultyLevel => Boolean(level));

  const uniqueLevels = Array.from(new Set(normalizedLevels));

  return uniqueLevels.sort((a, b) => {
    const aOrder = difficultyLevelOrderMap[a] ?? Number.MAX_SAFE_INTEGER;
    const bOrder = difficultyLevelOrderMap[b] ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });
};

// Get readable label for workout duration
export const getWorkoutDurationLabel = (duration: WorkoutDuration): string => {
  return duration;
};

// Get all training program type options for dropdowns
export const getTrainingProgramTypeOptions = (): Array<{
  value: TrainingProgramType;
  label: string;
}> => {
  return Object.values(TrainingProgramType).map((type) => ({
    value: type,
    label: getTrainingProgramTypeLabel(type),
  }));
};

// Get all difficulty level options for dropdowns
export const getDifficultyLevelOptions = (): Array<{
  value: DifficultyLevel;
  label: string;
}> => {
  return difficultyLevelOrder.map((level) => ({
    value: level,
    label: getDifficultyLevelLabel(level),
  }));
};

// Get all workout duration options for dropdowns
export const getWorkoutDurationOptions = (): Array<{
  value: WorkoutDuration;
  label: string;
}> => {
  return Object.values(WorkoutDuration).map((duration) => ({
    value: duration,
    label: getWorkoutDurationLabel(duration),
  }));
};

// Get color for program type badge
export const getTrainingProgramTypeColor = (
  programType: TrainingProgramType
): string => {
  const colors: Record<TrainingProgramType, string> = {
    [TrainingProgramType.CARDIO]: badge("red"),
    [TrainingProgramType.MOBILITY_AND_WARM_UP]: badge("green"),
    [TrainingProgramType.HYPERTROPHY]: badge("purple"),
    [TrainingProgramType.HYBRID]: badge("blue"),
    [TrainingProgramType.STRENGTH]: badge("orange"),
    [TrainingProgramType.OTHER]: badge("gray"),
  };

  return colors[programType];
};

// Get color for difficulty level badge
export const getDifficultyLevelColor = (
  difficultyLevel: DifficultyLevel | string
): string => {
  const colors: Record<DifficultyLevel, string> = {
    [DifficultyLevel.BEGINNER]: badge("green"),
    [DifficultyLevel.INTERMEDIATE]: badge("yellow"),
    [DifficultyLevel.ADVANCED]: badge("red"),
  };

  return (
    colors[difficultyLevel as DifficultyLevel] ||
    "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
  );
};
